import React from 'react';
import { FileText, Download, Eye, Truck } from 'lucide-react';

interface Document {
  type: 'bdc' | 'bdl' | 'facture';
  label: string;
  available: boolean;
  date?: string | null;
  onDownload: () => void;
  onView?: () => void;
}

interface OrderDocumentsProps {
  status: string;
  orderDate: string;
  shippedAt?: string | null;
  invoicedAt?: string | null;
  hasInvoice?: boolean;
  onDownloadBdc: () => void;
  onDownloadBdl: () => void;
  onDownloadInvoice: () => void;
}

const formatDate = (dateStr: string | null | undefined): string | null => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const OrderDocuments: React.FC<OrderDocumentsProps> = ({
  status,
  orderDate,
  shippedAt,
  invoicedAt,
  hasInvoice = false,
  onDownloadBdc,
  onDownloadBdl,
  onDownloadInvoice
}) => {
  const canShowBdl = ['shipped', 'delivered', 'invoiced', 'paid'].includes(status);
  const canShowInvoice = hasInvoice || ['invoiced', 'paid'].includes(status);

  const documents: Document[] = [
    {
      type: 'bdc',
      label: 'Bon de commande',
      available: true,
      date: orderDate,
      onDownload: onDownloadBdc,
      onView: onDownloadBdc
    },
    {
      type: 'bdl',
      label: 'Bon de livraison',
      available: canShowBdl,
      date: shippedAt,
      onDownload: onDownloadBdl,
      onView: onDownloadBdl
    },
    {
      type: 'facture',
      label: 'Facture',
      available: canShowInvoice,
      date: invoicedAt,
      onDownload: onDownloadInvoice,
      onView: onDownloadInvoice
    }
  ];

  const getDocumentIcon = (type: string) => {
    switch (type) {
      case 'bdl':
        return Truck;
      default:
        return FileText;
    }
  };

  const getDocumentColor = (type: string, available: boolean) => {
    if (!available) return 'bg-slate-100 dark:bg-slate-800 text-slate-400';

    switch (type) {
      case 'bdc':
        return 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800';
      case 'bdl':
        return 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800';
      case 'facture':
        return 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800';
      default:
        return 'bg-slate-50 dark:bg-slate-800 text-slate-600';
    }
  };

  return (
    <div className="space-y-3">
      {documents.map((doc) => {
        const DocIcon = getDocumentIcon(doc.type);
        const colorClasses = getDocumentColor(doc.type, doc.available);

        return (
          <div
            key={doc.type}
            className={`p-4 rounded-xl border transition-all ${doc.available
              ? `${colorClasses} hover:shadow-md`
              : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 opacity-60'
              }`}
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${doc.available
                    ? colorClasses.split(' ')[0] + ' ' + colorClasses.split(' ')[1]
                    : 'bg-slate-200 dark:bg-slate-700'
                    }`}
                >
                  <DocIcon className="w-5 h-5" />
                </div>
                <div>
                  <p className={`font-medium ${doc.available ? '' : 'text-slate-400 dark:text-slate-500'}`}>
                    {doc.label}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {doc.available && doc.date ? formatDate(doc.date) : 'Non disponible'}
                  </p>
                </div>
              </div>

              {doc.available && (
                <div className="flex items-center gap-2">
                  {doc.onView && (
                    <button
                      onClick={doc.onView}
                      className="p-2 rounded-lg hover:bg-white/50 dark:hover:bg-slate-700/50 transition-colors"
                      title="Voir"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={doc.onDownload}
                    className="p-2 rounded-lg hover:bg-white/50 dark:hover:bg-slate-700/50 transition-colors"
                    title="Télécharger"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default OrderDocuments;
